import {
  every, filter, find, includes, isNil, keys, map, orderBy, reduce, some, take, toLower,
} from 'lodash';
import { getSportLabelBySportId } from '@/services/helpers/mappings-mapper';
import { mapPlayerSetupBaseballGameParams } from '@/components/player-setup/params-helper';
import { baseballPlayerParamsSetup } from '@/services/helpers/player-setup-mapper';
import sportIds from '@/services/helpers/sports';
import { getInferModalSportConfig } from '@/components/player-setup/infer/infer-modal-config';

const { BASEBALL_ID } = sportIds;
const INFER_HITTERS_PER_TEAM = 9;

const PLACEHOLDER_PARAM_KEYS = ['LINE', 'PLAYER_INDEX', 'TEAM'];

const marketParamsMatch = (templateParams = {}, marketParams = {}) => {
  const significantKeys = filter(keys(templateParams), (key) => {
    const templateValue = templateParams[key];
    if (includes(PLACEHOLDER_PARAM_KEYS, key) && templateValue === 0) return false;
    return true;
  });

  if (!significantKeys.length) return true;

  return every(significantKeys, (key) => {
    const templateValue = templateParams[key];
    const marketValue = marketParams?.[key] ?? marketParams?.[key?.toLowerCase()];
    return templateValue === marketValue;
  });
};

export const getInferGameMarketCodes = (availableMarkets) => map(
  availableMarkets?.gameMarkets || [],
  (market) => market.marketCode,
);

export const getInferPlayerMarketCodesByType = (availableMarkets, playerType) => map(
  availableMarkets?.playerMarkets?.[playerType] || [],
  (market) => market.marketCode,
);

export const matchGameMarketToAvailable = (availableMarket, simulatedGameMarkets) => find(
  simulatedGameMarkets,
  (market) => market.marketCode === availableMarket.marketCode
    && marketParamsMatch(
      availableMarket.params,
      market.params || market.marketType?.params,
    ),
);

const getSelectionPrice = (price) => {
  if (!price) return null;
  const value = (price.probability + (price.bias || 0)) * 100;
  if (Number.isNaN(+value)) return null;
  return value % 1 !== 0 ? parseFloat(value?.toFixed(1)) : parseInt(value, 10);
};

const isMarketFilled = (market) => {
  if (!market || market.isError || market.isMainLineInvalid) return false;

  const hasMainLine = market?.marketSummary?.isMainLine;
  if (hasMainLine) {
    const mainLine = market?.marketSummary?.mainLine
      ?? market?.marketType?.params?.LINE
      ?? market?.params?.LINE;
    if (isNil(mainLine) || mainLine === '') return false;
  }

  const overSelection = find(market.selections, { selectionType: { selectionCode: 'OVER' } });
  if (overSelection) {
    const overPrice = getSelectionPrice(overSelection.price);
    if (isNil(overPrice) || overPrice === '') return false;
    if (overSelection.isError) return false;
  }

  const yesSelection = find(market.selections, { selectionType: { selectionCode: 'YES' } });
  if (yesSelection) {
    const yesPrice = getSelectionPrice(yesSelection.price);
    if (isNil(yesPrice) || yesPrice === '') return false;
    if (yesSelection.isError) return false;
  }

  const homeSelection = find(market.selections, { selectionType: { selectionCode: 'HOME' } });
  if (homeSelection && !overSelection && !yesSelection) {
    const homePrice = getSelectionPrice(homeSelection.price);
    if (isNil(homePrice) || homePrice === '') return false;
    if (homeSelection.isError) return false;
  }

  return true;
};

const mapMarketToParamFindingFormat = (market, sportLabel) => ({
  marketType: {
    marketCode: market.marketCode,
    sport: sportLabel,
    params: market.marketType?.params || market.params,
  },
  selections: map(market.selections, (selection) => ({
    selectionType: selection.selectionType,
    price: {
      probability: selection.price?.probability,
    },
  })),
});

export const getInferEligiblePlayersForTeam = (teamPlayers = []) => {
  const lineupPlayers = filter(teamPlayers, { lineup: true });
  const currentPitcher = find(lineupPlayers, (player) => player.pitcherParams?.isCurrentPitcher);
  const pitchers = currentPitcher ? [currentPitcher] : [];
  const hitters = take(
    orderBy(filter(lineupPlayers, { isHitter: true }), 'playerPositionIndex'),
    INFER_HITTERS_PER_TEAM,
  );

  return [...pitchers, ...hitters];
};

export const getInferEligiblePlayers = (tableData) => [
  ...getInferEligiblePlayersForTeam(tableData?.teamA),
  ...getInferEligiblePlayersForTeam(tableData?.teamB),
];

const teamHasCurrentPitcher = (teamPlayers = []) => !!find(
  filter(teamPlayers, { lineup: true }),
  (player) => player.pitcherParams?.isCurrentPitcher,
);

const getPlayerMarketByCode = (player, marketCode) => find(
  player.marketCodeDetails,
  (market) => market.marketCode === marketCode || market.simulatedMarket?.marketType?.marketCode === marketCode,
);

const playerHasAtLeastOneFilledMarket = (player, marketCodes) => {
  if (!marketCodes.length) return true;
  return some(marketCodes, (marketCode) => {
    const market = getPlayerMarketByCode(player, marketCode);
    return market?.simulatedMarket && isMarketFilled(market.simulatedMarket);
  });
};

export const validateInferModalMarkets = ({
  availableMarkets,
  paramsManagerMarkets,
  playerSetupTableData,
}) => {
  if (!availableMarkets || !paramsManagerMarkets || !playerSetupTableData) return false;

  const { simulatedGameMarkets } = paramsManagerMarkets;
  const gameMarketsToExclude = ['RUN_HANDICAP'];
  const filteredGameMarkets = filter(availableMarkets.gameMarkets, (market) => !includes(gameMarketsToExclude, market.marketCode));

  const gameMarketsValid = every(filteredGameMarkets || [], (availableMarket) => {
    const matchedMarket = matchGameMarketToAvailable(availableMarket, simulatedGameMarkets);
    return matchedMarket && isMarketFilled(matchedMarket);
  });

  if (!gameMarketsValid) return false;

  if (!teamHasCurrentPitcher(playerSetupTableData?.teamA)
    || !teamHasCurrentPitcher(playerSetupTableData?.teamB)) {
    return false;
  }

  const eligiblePlayers = getInferEligiblePlayers(playerSetupTableData);
  const pitcherMarketCodes = getInferPlayerMarketCodesByType(availableMarkets, 'PITCHER');
  const hitterMarketCodes = getInferPlayerMarketCodesByType(availableMarkets, 'HITTER');

  const pitchers = filter(eligiblePlayers, (player) => player.pitcherParams?.isCurrentPitcher);
  const hitters = filter(eligiblePlayers, { isHitter: true });

  const pitchersValid = every(pitchers, (player) => playerHasAtLeastOneFilledMarket(player, pitcherMarketCodes));

  if (!pitchersValid) return false;

  const hittersValid = every(hitters, (player) => playerHasAtLeastOneFilledMarket(player, hitterMarketCodes));

  return hittersValid;
};

export const buildBaseballParamFindingGameMarkets = ({
  availableMarkets,
  simulatedGameMarkets,
  sportLabel,
}) => reduce(
  availableMarkets?.gameMarkets || [],
  (allMarkets, availableMarket) => {
    const matchedMarket = matchGameMarketToAvailable(availableMarket, simulatedGameMarkets);
    if (!matchedMarket) return allMarkets;
    return [
      ...allMarkets,
      mapMarketToParamFindingFormat(matchedMarket, sportLabel),
    ];
  },
  [],
);

export const buildBaseballParamFindingPlayerMarkets = ({
  availableMarkets,
  playerSetupTableData,
  playerSetupData,
  sportLabel,
}) => {
  const eligiblePlayers = getInferEligiblePlayers(playerSetupTableData);
  const isUSAView = playerSetupData?.isUSAView;

  return reduce(eligiblePlayers, (allPlayerMarkets, player) => {
    const playerType = player.pitcherParams?.isCurrentPitcher ? 'PITCHER' : 'HITTER';
    const requiredMarketCodes = getInferPlayerMarketCodesByType(availableMarkets, playerType);
    const teamIndex = (() => {
      const isTeamA = some(playerSetupTableData.teamA, { playerId: player.playerId });
      const isHome = isUSAView ? !isTeamA : isTeamA;
      return isHome ? 0 : 1;
    })();

    const markets = reduce(requiredMarketCodes, (playerMarkets, marketCode) => {
      const market = getPlayerMarketByCode(player, marketCode);
      if (!market?.simulatedMarket) return playerMarkets;

      const simulatedMarket = {
        ...market.simulatedMarket,
        marketCode: market.marketCode || market.simulatedMarket?.marketType?.marketCode,
        params: {
          ...(market.simulatedMarket?.params || market.simulatedMarket?.marketType?.params),
          PLAYER_INDEX: player.playerIndex,
          TEAM: teamIndex,
        },
      };

      return [
        ...playerMarkets,
        mapMarketToParamFindingFormat(simulatedMarket, sportLabel),
      ];
    }, []);

    if (!markets.length) return allPlayerMarkets;

    return [
      ...allPlayerMarkets,
      {
        playerIndex: player.playerIndex,
        markets,
      },
    ];
  }, []);
};

export const getInferModalTeamPlayers = ({
  teamPlayers,
  playerTypeTab,
  playerSearchValue,
  sportId,
}) => {
  let players = filter(teamPlayers || [], { lineup: true });

  if (sportId === BASEBALL_ID) {
    if (playerTypeTab === 'pitchers') {
      const currentPitcher = find(players, (player) => player.pitcherParams?.isCurrentPitcher);
      players = currentPitcher ? [currentPitcher] : [];
    } else if (playerTypeTab === 'hitters') {
      players = take(orderBy(filter(players, { isHitter: true }), 'playerPositionIndex'), INFER_HITTERS_PER_TEAM);
    }
  } else {
    const sportConfig = getInferModalSportConfig(sportId);
    const activePlayerTypeTab = find(sportConfig?.playerTypeTabs, (tab) => tab.key === playerTypeTab);
    if (activePlayerTypeTab?.filter) {
      players = filter(players, activePlayerTypeTab.filter);
    }
  }

  if (playerSearchValue) {
    const search = playerSearchValue.toLowerCase();
    players = filter(players, (player) => includes(toLower(player.playerName), search));
  }

  return players;
};

export const filterGameMarketsForInfer = (gameMarkets, availableMarkets) => {
  const availableCodes = getInferGameMarketCodes(availableMarkets);
  return filter(gameMarkets, (market) => {
    const marketCode = market.currentMarket?.marketCode || market.marketCode;
    return includes(availableCodes, marketCode);
  });
};

export const mapBaseballAsyncInferPayload = ({
  eventData,
  gameParams,
  playerSetupTableData,
  paramsManagerMarkets,
  availableMarkets,
}) => {
  const sportLabel = getSportLabelBySportId(eventData.sportId);
  const { isUSAView } = eventData;
  const firstTeam = isUSAView ? playerSetupTableData.teamB : playerSetupTableData.teamA;
  const secondTeam = isUSAView ? playerSetupTableData.teamA : playerSetupTableData.teamB;

  return {
    paramFindingMarkets: buildBaseballParamFindingGameMarkets({
      availableMarkets,
      simulatedGameMarkets: paramsManagerMarkets.simulatedGameMarkets,
      sportLabel,
    }),
    gameParams: {
      eventId: eventData.eventId,
      sport: 'baseball',
      ...mapPlayerSetupBaseballGameParams(gameParams),
    },
    playerMarkets: buildBaseballParamFindingPlayerMarkets({
      availableMarkets,
      playerSetupTableData,
      playerSetupData: eventData,
      sportLabel,
    }),
    homeTeam: {
      playersParams: map(
        firstTeam,
        (playerData) => baseballPlayerParamsSetup(playerData),
      ),
    },
    awayTeam: {
      playersParams: map(
        secondTeam,
        (playerData) => baseballPlayerParamsSetup(playerData),
      ),
    },
  };
};
